import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { leadsApi, usersApi } from '../api';
import { ArrowLeft, Pencil, Save, X, Trash2, Plus, UserCheck, CheckSquare, Building2, KanbanSquare, UserCircle } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import TaskModal from '../components/TaskModal';
import dayjs from 'dayjs';

const statusLabels: Record<string, string> = { NEW: 'Новый', IN_WORK: 'В работе', QUALIFIED: 'Квалифицирован', CONVERTED: 'Конвертирован', REJECTED: 'Отклонен' };
const statusBadgeColor: Record<string, string> = { NEW: 'badge-info', IN_WORK: 'badge-warning', QUALIFIED: 'badge-success', CONVERTED: 'badge-success', REJECTED: 'badge-danger' };
const taskStatusLabel: Record<string, string> = { PENDING: 'Ожидает', IN_PROGRESS: 'В работе', COMPLETED: 'Выполнена', CANCELLED: 'Отменена' };
const taskStatusColor: Record<string, string> = { PENDING: 'badge-warning', IN_PROGRESS: 'badge-info', COMPLETED: 'badge-success', CANCELLED: 'badge-default' };

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await leadsApi.getById(id);
      setLead(res.data);
      setForm({
        firstName: res.data.firstName || '',
        lastName: res.data.lastName || '',
        inn: res.data.inn || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        companyName: res.data.companyName || '',
        position: res.data.position || '',
        status: res.data.status || 'NEW',
        source: res.data.source || '',
        description: res.data.description || '',
        tags: res.data.tags?.join(', ') || '',
        assignedId: res.data.assignedId || '',
      });
    } catch (e) {
      console.error('Load lead error:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await usersApi.list({ limit: 100 });
        setUsers(res.data.data || []);
      } catch {}
    };
    loadUsers();
  }, []);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        inn: form.inn || null,
        tags: form.tags.split(',').map((s: string) => s.trim()).filter(Boolean),
        assignedId: form.assignedId || null,
      };
      await leadsApi.update(id, data);
      setEditing(false);
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Удалить лид?')) return;
    try {
      await leadsApi.remove(id);
      navigate('/leads');
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  const handleConvert = async () => {
    if (!id || !confirm('Конвертировать лид в компанию, контакт и сделку?')) return;
    try {
      await leadsApi.convert(id);
      load();
    } catch (e) {
      alert('Ошибка конвертации');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>;
  if (!lead) return <div style={{ padding: 40, textAlign: 'center' }}>Лид не найден</div>;

  const sourceOptions = ['Сайт', 'Звонок', 'Email', 'Соцсети', 'Рекомендация', 'Выставка', 'Другое'];

  return (
    <div>
      <div className="detail-header">
        <button className="detail-back-btn" onClick={() => navigate('/leads')}>
          <ArrowLeft size={20} />
        </button>
        <div className="detail-header-info">
          <div className="detail-header-title">{lead.firstName} {lead.lastName}</div>
          <div className="detail-header-subtitle">
            <span className={`badge ${statusBadgeColor[lead.status] || 'badge-default'}`}>{statusLabels[lead.status] || lead.status}</span>
            {lead.companyName && <span style={{ marginLeft: 8, color: 'var(--color-text-secondary)' }}>{lead.companyName}</span>}
          </div>
        </div>
        <div className="detail-header-actions">
          {editing ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                <Save size={16} /> {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); load(); }}>
                <X size={16} /> Отмена
              </button>
            </>
          ) : (
            <>
              {lead.status !== 'CONVERTED' && (
                <button className="btn btn-primary btn-sm" onClick={handleConvert}>
                  <UserCheck size={16} /> Конвертировать
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={16} /> Редактировать
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                <Trash2 size={16} /> Удалить
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-grid-main">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Информация о лиде</div>
            </div>
            <div className="card-body">
              {editing ? (
                <>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Имя</label>
                      <input className="form-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Фамилия</label>
                      <input className="form-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">ИНН</label>
                      <input className="form-input" value={form.inn} onChange={e => setForm({ ...form, inn: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Телефон</label>
                      <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Компания</label>
                      <input className="form-input" value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Должность</label>
                      <input className="form-input" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Статус</label>
                      <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                        <option value="NEW">Новый</option>
                        <option value="IN_WORK">В работе</option>
                        <option value="QUALIFIED">Квалифицирован</option>
                        <option value="CONVERTED">Конвертирован</option>
                        <option value="REJECTED">Отклонен</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Источник</label>
                      <select className="form-select" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                        <option value="">— Не выбрано —</option>
                        {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <SearchableSelect
                        label="Ответственный"
                        value={form.assignedId}
                        onChange={val => setForm({ ...form, assignedId: val })}
                        options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                        emptyLabel="— Не назначен —"
                      />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Описание</label>
                      <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Теги (через запятую)</label>
                      <input className="form-input" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="detail-field-grid">
                  <div className="detail-field">
                    <div className="detail-field-label">Имя</div>
                    <div className="detail-field-value">{lead.firstName || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Фамилия</div>
                    <div className="detail-field-value">{lead.lastName || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">ИНН</div>
                    <div className="detail-field-value">{lead.inn || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Статус</div>
                    <div className="detail-field-value"><span className={`badge ${statusBadgeColor[lead.status] || 'badge-default'}`}>{statusLabels[lead.status] || lead.status}</span></div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Email</div>
                    <div className="detail-field-value">{lead.email || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Телефон</div>
                    <div className="detail-field-value">{lead.phone || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Компания</div>
                    <div className="detail-field-value">{lead.companyName || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Должность</div>
                    <div className="detail-field-value">{lead.position || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Источник</div>
                    <div className="detail-field-value">{lead.source ? <span className="badge badge-default">{lead.source}</span> : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Ответственный</div>
                    <div className="detail-field-value">{lead.assigned ? `${lead.assigned.firstName} ${lead.assigned.lastName}` : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Описание</div>
                    <div className="detail-field-value">{lead.description || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Теги</div>
                    <div className="detail-field-value">
                      {lead.tags?.length ? (
                        <div className="detail-tags">{lead.tags.map((t: string, i: number) => <span key={i} className="detail-tag">{t}</span>)}</div>
                      ) : <span className="empty">—</span>}
                    </div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Создано</div>
                    <div className="detail-field-value">{dayjs(lead.createdAt).format('DD.MM.YYYY HH:mm')}</div>
                  </div>
                  {lead.convertedAt && (
                    <div className="detail-field">
                      <div className="detail-field-label">Конвертировано</div>
                      <div className="detail-field-value">{dayjs(lead.convertedAt).format('DD.MM.YYYY HH:mm')}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {lead.status === 'CONVERTED' && (lead.convertedToCompanyId || lead.convertedToContactId || lead.convertedToDealId) && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Конвертировано в</div>
              </div>
              <div className="card-body" style={{ padding: 12 }}>
                {lead.convertedToCompanyId && (
                  <Link to={`/companies/${lead.convertedToCompanyId}`} className="detail-related-item">
                    <div className="detail-related-item-info">
                      <div className="detail-related-item-title"><Building2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Компания</div>
                    </div>
                  </Link>
                )}
                {lead.convertedToContactId && (
                  <Link to={`/contacts/${lead.convertedToContactId}`} className="detail-related-item">
                    <div className="detail-related-item-info">
                      <div className="detail-related-item-title"><UserCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Контакт</div>
                    </div>
                  </Link>
                )}
                {lead.convertedToDealId && (
                  <Link to={`/deals/${lead.convertedToDealId}`} className="detail-related-item">
                    <div className="detail-related-item-info">
                      <div className="detail-related-item-title"><KanbanSquare size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Сделка</div>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="detail-grid-sidebar">
          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckSquare size={16} /> Задачи ({lead.tasks?.length || 0})
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowTaskModal(true)}>
                <Plus size={14} /> Добавить
              </button>
            </div>
            <div className="card-body" style={{ padding: lead.tasks?.length ? 12 : 24 }}>
              {lead.tasks?.length ? lead.tasks.map((t: any) => (
                <Link key={t.id} to={`/tasks/${t.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title" style={{ textDecoration: t.isCompleted ? 'line-through' : 'none' }}>{t.title}</div>
                    <div className="detail-related-item-meta">
                      {t.dueDate ? dayjs(t.dueDate).format('DD.MM.YYYY') : '—'} · {t.assignee?.firstName} {t.assignee?.lastName}
                    </div>
                  </div>
                  <div className="detail-related-item-badge">
                    <span className={`badge ${taskStatusColor[t.status] || 'badge-default'}`}>{taskStatusLabel[t.status] || t.status}</span>
                  </div>
                </Link>
              )) : <div className="detail-related-empty">Нет задач</div>}
            </div>
          </div>
        </div>
      </div>

      {showTaskModal && (
        <TaskModal
          leadId={id}
          onClose={() => setShowTaskModal(false)}
          onSave={() => { setShowTaskModal(false); load(); }}
        />
      )}
    </div>
  );
}
